/*
 * Mission Watch — Per-organization Report History browser.
 *
 * Two-pane layout:
 *  • Left: latest up to 20 DEEP scans (Iter-75). Deep scans only —
 *          hourly monitoring lives in the Monitoring Log at the bottom.
 *  • Right: full detail view of the selected report (score, categories,
 *           checks, AI summary, recommendations). Same rendering DNA as
 *           the Dashboard's health-report card so users don't have to
 *           relearn anything.
 *
 * Iter-75 sourcing:
 *   `deep_history`   — powers the left rail (deep-only, up to 20).
 *   `monitoring_log` — powers the collapsed bottom section (non-deep).
 *   Falls back to the deprecated `history` field for older backends.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ShieldCheck, ArrowLeft, CheckCircle2, AlertTriangle, XCircle, MinusCircle,
  Sparkles, Lightbulb, LifeBuoy, Clock, LogOut, Share2, Link2, Copy, EyeOff, ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";
import { LoadingScreen } from "../components/LoadingScreen";


const SCORE_STYLES = {
  green: "bg-emerald-50 border-emerald-100 text-emerald-600",
  amber: "bg-amber-50 border-amber-100 text-amber-600",
  red: "bg-red-50 border-red-100 text-red-600",
  info: "bg-charcoal-100 border-black/5 text-charcoal-400",
};


function StatusIcon({ status }) {
  if (status === "green") return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={2} />;
  if (status === "amber") return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" strokeWidth={2} />;
  if (status === "red") return <XCircle className="w-4 h-4 text-red-500 shrink-0" strokeWidth={2} />;
  return <MinusCircle className="w-4 h-4 text-charcoal-300 shrink-0" strokeWidth={2} />;
}


function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}


export default function ReportHistory() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [history, setHistory] = useState(null);
  const [selectedId, setSelectedId] = useState(params.get("id"));
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  // "Hourly monitoring scans" section — collapsed by default. Most customers
  // never expand it; it exists as an escape hatch for owners debugging why
  // a specific hourly sweep produced a specific number.
  const [showBasic, setShowBasic] = useState(false);
  const [sharing, setSharing] = useState(false);

  const backendOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = report?.share_token ? `${backendOrigin}/r/${report.share_token}` : null;

  const toggleShare = async () => {
    if (!report?.id) return;
    setSharing(true);
    try {
      if (report.share_token) {
        await api.post(`/reports/${report.id}/unshare`);
        setReport({ ...report, share_token: null, shared_at: null });
        toast.success("Sharing turned off — the public link no longer works.");
      } else {
        const { data } = await api.post(`/reports/${report.id}/share`);
        setReport({ ...report, share_token: data.share_token, shared_at: data.shared_at });
      }
    } catch (err) {
      toast.error("Couldn't update sharing. Please try again.");
    } finally { setSharing(false); }
  };

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Public link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — the link is: " + shareUrl);
    }
  };

  useEffect(() => {
    api.get("/reports/history").then(({ data }) => {
      setHistory(data);
      // Iter-75: default selection is the LATEST deep scan. Falls back
      // to the deprecated combined `history` list for older backends,
      // preserving behaviour during the deploy cutover.
      const deep = data.deep_history || (data.history || []).filter((h) => h.scan_depth === "deep");
      if (!selectedId && deep.length) {
        setSelectedId(deep[0].id);
      } else if (!selectedId && data.history?.length) {
        setSelectedId(data.history[0].id);
      }
    }).catch(() => setHistory({ history: [] }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedId) return;
    setLoadingReport(true);
    setReport(null);
    api.get(`/reports/${selectedId}`)
      .then(({ data }) => setReport(data))
      .catch(() => setReport({ error: true }))
      .finally(() => setLoadingReport(false));
    setParams({ id: selectedId }, { replace: true });
  }, [selectedId, setParams]);

  // Iter-75 — deep_history / monitoring_log are the primary sources.
  // Fall back to the deprecated combined `history` list only when the
  // backend hasn't shipped the new fields yet (deploy cutover safety).
  const allItems = history?.history ?? [];
  const items = (history?.deep_history)
    ?? allItems.filter((h) => h.scan_depth === "deep").slice(0, 20);
  const basicItems = (history?.monitoring_log)
    ?? allItems.filter((h) => h.scan_depth !== "deep");

  // Bulletproof first paint — no placeholder while /reports/history is in flight.
  if (history === null) return <LoadingScreen label="Loading your report history…" />;

  return (
    <div className="min-h-screen bg-charcoal-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900 text-[15px]">Mission Watch</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <a href="mailto:admin@missionwatch.app?subject=Mission%20Watch%20Support" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors" data-testid="history-support-link">
              <LifeBuoy className="w-4 h-4" /> Support
            </a>
            <button onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-6" data-testid="report-history-page">
        <div className="mb-4">
          <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">History</p>
          <h1 className="text-3xl font-semibold tracking-tight text-charcoal-900 mt-1">Report History</h1>
          <p className="text-sm text-charcoal-500 mt-2 max-w-2xl">
            Every deep Mission Watch Score we've run for {user?.email}. Click any run to see what was
            healthy and what needed attention on that day.
          </p>
        </div>

        {/* Iter-75 — Score Trend chart removed. The two-pane grid now
            sits directly under the page header so deep-scan history +
            selected report are visible without scrolling past chart
            noise. */}

        <div className="grid lg:grid-cols-3 gap-6 min-w-0">
          {/* left rail: history list — on mobile it renders BELOW the
              detail (order-2) so the customer sees the selected
              report first and only scrolls to the list to pick a
              different run. On desktop the ordering is neutral, so
              the standard "list first, detail second" layout is
              preserved. `min-w-0` on both children prevents grid
              tracks from stretching beyond the viewport when a piece
              of content (long URL, long recommendation line, etc.)
              refuses to break. */}
          <aside className="order-2 lg:order-1 lg:col-span-1 min-w-0 rounded-2xl bg-white border border-black/5 shadow-soft p-4 sm:p-5 h-fit lg:sticky lg:top-24 overflow-hidden" data-testid="history-list">
            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-500 mb-3">
              {items.length} deep scan{items.length === 1 ? "" : "s"}
            </p>
            {items.length === 0 && (
              <p className="text-sm text-charcoal-400 py-6 text-center">
                Your first Mission Watch Health Report will be available after your initial weekly review. Run your first Mission Watch Score from the dashboard to get started.
              </p>
            )}
            <ul className="space-y-2">
              {items.map((h) => {
                const isSelected = h.id === selectedId;
                return (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(h.id)}
                      data-testid={`history-item-${h.id}`}
                      className={`w-full text-left flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl border transition-colors ${
                        isSelected
                          ? "bg-charcoal-900 border-charcoal-900 text-white"
                          : "bg-white border-black/5 hover:border-black/15 hover:bg-charcoal-50/70"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-white" : "text-charcoal-800"}`}>
                          {h.period || fmtDate(h.date)}
                        </p>
                        <p className={`text-[11px] font-mono mt-0.5 truncate ${isSelected ? "text-white/60" : "text-charcoal-400"}`}>
                          {fmtDate(h.date)}
                          {(h.scan_depth === "deep" || (h.trigger && String(h.trigger).endsWith("_fallback"))) && ` · ${h.pages_scanned ?? "?"} pages`}
                        </p>
                      </div>
                      <div className={`flex flex-col items-end shrink-0`}>
                        <span className={`text-lg font-semibold tracking-tighter ${isSelected ? "text-white" : "text-charcoal-900"}`}>
                          {h.score ?? "—"}
                        </span>
                        <span className={`text-[10px] font-mono uppercase tracking-wider ${
                          h.status === "green" ? "text-emerald-400" :
                          h.status === "amber" ? "text-amber-500" :
                          h.status === "red" ? "text-red-500" : "text-charcoal-400"
                        }`}>{h.status}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* right column: full detail of the currently selected
              deep scan. On mobile this renders FIRST (order-1) so
              the customer sees their report immediately; the list
              below acts as a compact chooser to jump to older runs. */}
          <section className="order-1 lg:order-2 lg:col-span-2 min-w-0 rounded-2xl bg-white border border-black/5 shadow-soft p-5 sm:p-6 overflow-hidden" data-testid="history-detail">
            {loadingReport && <p className="text-sm text-charcoal-400 py-6 font-mono text-center">Loading report…</p>}
            {!loadingReport && report?.error && <p className="text-sm text-red-500 py-6 text-center">Couldn't load that report.</p>}
            {!loadingReport && report && !report.error && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">
                      {report.period || fmtDate(report.date)} · {report.scan_depth === "deep" ? "Deep scan" : "Standard scan"}
                    </p>
                    <h2 className="text-lg font-medium text-charcoal-900 mt-0.5">Mission Watch Score</h2>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={toggleShare}
                      disabled={sharing}
                      className={`inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors disabled:opacity-60 ${
                        report.share_token
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          : "border-black/10 text-charcoal-700 hover:bg-charcoal-50"
                      }`}
                      data-testid="share-report-btn"
                      title={report.share_token ? "Public sharing is on" : "Create a shareable public link"}
                    >
                      {report.share_token ? <EyeOff className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                      {sharing ? "…" : report.share_token ? "Stop sharing" : "Share"}
                    </button>
                    {report.share_token && (
                      <button
                        type="button"
                        onClick={copyShareLink}
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-charcoal-900 text-white hover:bg-charcoal-800 transition-colors"
                        data-testid="copy-share-link-btn"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy link
                      </button>
                    )}
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${SCORE_STYLES[report.overall_status] || SCORE_STYLES.info}`} data-testid="history-detail-score">
                      <span className="text-2xl font-semibold text-charcoal-900 tracking-tighter">{report.overall_score ?? "—"}</span>
                      <span className="text-xs font-mono">/100</span>
                    </div>
                  </div>
                </div>
                {report.share_token && shareUrl && (
                  <div className="mb-4 rounded-xl bg-emerald-50/70 border border-emerald-100 px-4 py-3 flex flex-wrap items-center gap-2 text-xs" data-testid="share-active-banner">
                    <Link2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span className="font-medium text-emerald-800">Public link active:</span>
                    <a href={shareUrl} target="_blank" rel="noreferrer" className="font-mono text-emerald-700 hover:underline break-all">{shareUrl}</a>
                  </div>
                )}
                <div className="mb-5 rounded-xl bg-charcoal-50/70 border border-black/5 px-4 py-3 flex items-center gap-2 text-xs font-mono text-charcoal-500">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{fmtDate(report.date)}</span>
                  {report.pages_scanned ? <span className="ml-auto">{report.pages_scanned} pages scanned</span> : null}
                </div>
                {report.ai_summary && (
                  <div className="mb-5 rounded-xl bg-charcoal-900 text-white p-5" data-testid="history-ai-summary">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-emerald-400" />
                      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/50">Mission Watch Summary</p>
                    </div>
                    <p className="text-sm leading-relaxed text-white/90">{report.ai_summary}</p>
                  </div>
                )}
                {report.recommendations?.length > 0 && (
                  <div className="mb-5 rounded-xl bg-amber-50/70 border border-amber-100 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-4 h-4 text-amber-600" />
                      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-amber-700">Recommended Improvements</p>
                    </div>
                    <ul className="space-y-2">
                      {report.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-charcoal-700 leading-snug">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="space-y-5">
                  {(report.categories?.length ? report.categories : [{ key: null, name: "Checks", status: report.overall_status }]).map((cat) => {
                    const catChecks = cat.key
                      ? (report.checks || []).filter((c) => c.category === cat.key)
                      : (report.checks || []);
                    if (!catChecks.length) return null;
                    return (
                      <div key={cat.key || "all"}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${cat.status === "green" ? "bg-emerald-500" : cat.status === "amber" ? "bg-amber-500" : cat.status === "red" ? "bg-red-500" : "bg-charcoal-300"}`} />
                            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-500">{cat.name}</p>
                          </div>
                          {cat.total != null && (
                            <span className="text-[11px] font-mono text-charcoal-400">{cat.passing}/{cat.total} passing</span>
                          )}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {catChecks.map((c) => (
                            <div key={c.key} className="flex items-start gap-3 py-2.5 px-3.5 rounded-xl border border-black/5 bg-charcoal-50/70">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-charcoal-800 leading-tight">{c.label}</p>
                                <p className="text-[11px] font-mono text-charcoal-400 mt-0.5 truncate">{c.detail}</p>
                              </div>
                              <StatusIcon status={c.status} />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {!loadingReport && !report && !selectedId && (
              <p className="text-sm text-charcoal-400 py-12 text-center">Pick a report on the left to see its details.</p>
            )}
          </section>
        </div>

        {/* Hourly monitoring scans — collapsed by default.
            Most nonprofits care about the deep month-over-month picture, not
            the every-hour heartbeat. This section is deliberately downplayed
            (muted colors, low-density type) so it feels like exactly what it
            is: a debug drawer for the rare case where someone wants to see
            what an individual hourly sweep found. */}
        {basicItems.length > 0 && (
          <div className="mt-10 rounded-2xl bg-white/60 border border-black/5 p-4 sm:p-5" data-testid="basic-scans-section">
            <button
              type="button"
              onClick={() => setShowBasic((v) => !v)}
              className="w-full flex items-center justify-between text-left"
              data-testid="basic-scans-toggle"
              aria-expanded={showBasic}
            >
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Monitoring log</p>
                <p className="text-sm text-charcoal-700 mt-0.5">
                  Hourly monitoring scans <span className="text-charcoal-400">— {basicItems.length} entr{basicItems.length === 1 ? "y" : "ies"}</span>
                </p>
              </div>
              <ChevronDown className={`w-4 h-4 text-charcoal-400 transition-transform ${showBasic ? "rotate-180" : ""}`} />
            </button>
            {showBasic && (
              <>
                <p className="text-[12px] text-charcoal-500 mt-3 leading-relaxed max-w-2xl">
                  These are the lightweight hourly checks we run in the background — homepage load, HTTPS, donation-page reachability. Useful for spotting exactly when an issue started; less useful than the full monthly deep scans above.
                </p>
                <ul className="mt-4 space-y-1.5" data-testid="basic-scans-list">
                  {basicItems.map((h) => {
                    const isSelected = h.id === selectedId;
                    return (
                      <li key={h.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(h.id)}
                          data-testid={`basic-scan-item-${h.id}`}
                          className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                            isSelected
                              ? "bg-charcoal-900 text-white"
                              : "text-charcoal-600 hover:bg-charcoal-100"
                          }`}
                        >
                          <span className={`text-[13px] font-mono ${isSelected ? "text-white/90" : "text-charcoal-700"}`}>
                            {fmtDate(h.date)}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className={`text-[11px] font-mono uppercase tracking-wider ${
                              h.status === "green" ? "text-emerald-500" :
                              h.status === "amber" ? "text-amber-500" :
                              h.status === "red" ? "text-red-500" : (isSelected ? "text-white/60" : "text-charcoal-400")
                            }`}>{h.status || "—"}</span>
                            <span className={`text-[13px] font-semibold tabular-nums ${isSelected ? "text-white" : "text-charcoal-700"}`}>
                              {h.score ?? "—"}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
